import React from 'react';
import styled from 'styled-components';
import { media } from '../../theme/tokens';
import { KpiStrip } from '../../components/KpiStrip';
import { EmptyState } from '../../components/EmptyState';
import { HorizontalBarChart } from '../../components/HorizontalBarChart';
import { NegoceFilterBar } from './NegoceFilterBar';
import { useNegoceFilters } from './NegoceFilters';
import {
  useSynthesisKpi,
  useSynthesisSalesByCountry,
  useSynthesisSalesByWine
} from 'apiTyped/hooks/negoce.query';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatNumber = (n: number | undefined | null): string =>
  n != null ? n.toLocaleString('fr-FR') : '—';

const formatPercent = (n: number | undefined | null): string =>
  n != null ? `${n.toFixed(1)}%` : '—';

// ─── Component ──────────────────────────────────────────────────────────────

export const NegoceSynthesis: React.FC = () => {
  const { campaignId, wineId, saleTypeId } = useNegoceFilters();

  const { isLoadingSynthesisKpi, synthesisKpi } = useSynthesisKpi({
    campaignId,
    saleTypeId,
    wineId
  });

  const { isLoadingSynthesisSalesByCountry, synthesisSalesByCountry } =
    useSynthesisSalesByCountry({
      campaignId,
      saleTypeId,
      wineId
    });

  const { isLoadingSynthesisSalesByWine, synthesisSalesByWine } =
    useSynthesisSalesByWine({
      campaignId,
      saleTypeId
    });

  const kpis = synthesisKpi as any;
  const byCountry = (synthesisSalesByCountry as any)?.data || [];
  const byWine = (synthesisSalesByWine as any)?.data || [];

  const kpiItems = [
    {
      label: 'Bottles sold (eq. 75cl)',
      value: isLoadingSynthesisKpi ? '...' : formatNumber(kpis?.bottles_sold),
      delta: kpis?.bottles_sold_n1
        ? {
            value: `${formatNumber(kpis.bottles_sold_n1)} N-1`,
            direction: 'flat' as const
          }
        : undefined
    },
    {
      label: 'Number of customers',
      value: isLoadingSynthesisKpi ? '...' : formatNumber(kpis?.customers_count)
    },
    {
      label: 'Exclusivity rate',
      value: isLoadingSynthesisKpi
        ? '...'
        : formatPercent(kpis?.exclusivity_rate),
      delta:
        kpis?.exclusivity_rate != null
          ? { value: 'Allocation sold exclusively', direction: 'up' as const }
          : undefined
    },
    {
      label: 'Resale rate',
      value: isLoadingSynthesisKpi ? '...' : formatPercent(kpis?.resale_rate),
      delta:
        kpis?.resale_rate != null
          ? {
              value: 'Over allocation',
              direction:
                kpis.resale_rate > 100 ? ('up' as const) : ('down' as const)
            }
          : undefined
    },
    {
      label: 'Loyalty rate',
      value: isLoadingSynthesisKpi ? '...' : formatPercent(kpis?.loyalty_rate),
      delta:
        kpis?.loyalty_rate != null
          ? { value: 'Recurring customers', direction: 'up' as const }
          : undefined
    },
    {
      label: 'Bottles sold N-1',
      value: isLoadingSynthesisKpi ? '...' : formatNumber(kpis?.bottles_sold_n1)
    }
  ];

  return (
    <Page>
      <Header>
        <Title>Synthesis</Title>
        <Subtitle>
          Key performance indicators for your négoce campaign. Filter by
          campaign, wine, and sale type.
        </Subtitle>
      </Header>

      <NegoceFilterBar />

      <KpiStrip items={kpiItems} />

      {/* Sales by Country chart */}
      <ChartSection>
        <ChartTitle>Sales by country</ChartTitle>
        <ChartMeta>Bottles sold (eq. 75cl) — current vs N-1</ChartMeta>
        {isLoadingSynthesisSalesByCountry ? (
          <LoadingState>Loading...</LoadingState>
        ) : byCountry.length > 0 ? (
          <HorizontalBarChart
            data={byCountry.map((row: any) => ({
              label: row.label || row.country_name,
              value: row.value,
              previousValue: row.value_n1 || 0
            }))}
            formatValue={formatNumber}
            legend={['Current', 'N-1']}
          />
        ) : (
          <EmptyState>No data available for this selection</EmptyState>
        )}
      </ChartSection>

      {/* Sales by Wine chart */}
      <ChartSection>
        <ChartTitle>Sales by wine</ChartTitle>
        <ChartMeta>Year-over-year comparison</ChartMeta>
        {isLoadingSynthesisSalesByWine ? (
          <LoadingState>Loading...</LoadingState>
        ) : byWine.length > 0 ? (
          <HorizontalBarChart
            data={byWine.map((row: any) => ({
              label: row.label || row.wine_name,
              value: row.value,
              previousValue: row.value_n1 || 0
            }))}
            formatValue={formatNumber}
            legend={['Current', 'N-1']}
          />
        ) : (
          <EmptyState>No data available for this selection</EmptyState>
        )}
      </ChartSection>
    </Page>
  );
};

// ─── Styled ─────────────────────────────────────────────────────────────────

const Page = styled.div`
  max-width: 1200px;
`;

const Header = styled.header`
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: var(--font-serif);
  font-size: 28px;
  font-weight: 500;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: var(--ink-muted);
  margin: 0;
  max-width: 640px;
  line-height: 1.5;
`;

const ChartSection = styled.section`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 20px 22px;
  margin-bottom: 20px;
`;

const ChartTitle = styled.div`
  font-family: var(--font-serif);
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 4px;
`;

const ChartMeta = styled.div`
  font-size: 12px;
  color: var(--ink-faint);
  margin-bottom: 20px;
`;

const LoadingState = styled.div`
  padding: 32px 0;
  text-align: center;
  color: var(--ink-faint);
  font-size: 13px;
`;
